import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';
import { LOGIN_PATH } from '@/lib/constants';

const CONFIRM_WORD = 'verwijder';

export function DeleteAccountSection() {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const canConfirm = confirmText.trim().toLowerCase() === CONFIRM_WORD;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke('delete-account');
      if (error) throw error;

      await supabase.auth.signOut();
      window.location.href = LOGIN_PATH;
    } catch (err) {
      console.error('Account deletion failed:', err);
      toast({
        variant: 'destructive',
        title: 'Verwijderen mislukt',
        description:
          'We konden je account niet verwijderen. Probeer het opnieuw of neem contact op.',
      });
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-8 pt-6 border-t border-border">
      <div className="flex flex-col items-center gap-2 text-center">
        <h4 className="text-sm font-medium text-muted-foreground">
          Account verwijderen
        </h4>
        <p className="text-xs text-muted-foreground max-w-md">
          Je account en al je gegevens worden gepland voor definitieve
          verwijdering. Je hebt 30 dagen om te herstellen door opnieuw in te
          loggen.
        </p>
        <Button
          variant="ghost"
          size="sm"
          className="text-red-500 hover:text-red-600 hover:bg-red-50"
          onClick={() => setOpen(true)}
        >
          <Trash2 className="mr-2 h-4 w-4" />
          Verwijder mijn account
        </Button>
      </div>

      <AlertDialog
        open={open}
        onOpenChange={(next) => {
          if (!submitting) {
            setOpen(next);
            if (!next) setConfirmText('');
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Account definitief verwijderen?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p>
                  Je account, chatberichten, favorieten en activiteit worden
                  over 30 dagen permanent verwijderd. Je wordt nu uitgelogd.
                </p>
                <p>
                  Wil je het toch niet? Log dan binnen 30 dagen opnieuw in om je
                  account te herstellen.
                </p>
                <p>
                  Typ{' '}
                  <span className="font-mono font-semibold text-red-600">
                    {CONFIRM_WORD}
                  </span>{' '}
                  hieronder om te bevestigen.
                </p>
                <Input
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={CONFIRM_WORD}
                  autoComplete="off"
                  disabled={submitting}
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting}>
              Annuleren
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={!canConfirm || submitting}
              onClick={handleConfirm}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {submitting ? 'Bezig...' : 'Verwijder mijn account'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
